import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { useAuth } from './useAuth';

/**
 * App-level auth wall. Mounts the app only once a Cognito session is valid.
 * When Cognito isn't configured (local dev) `status` is 'disabled' and the gate
 * is a pass-through. Renders its own on-brand forms — no Cognito Hosted UI.
 */
export function LoginGate({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  const bootstrap = useAuth((s) => s.bootstrap);

  useEffect(() => { void bootstrap(); }, [bootstrap]);

  if (status === 'disabled' || status === 'authenticated') return <>{children}</>;
  if (status === 'loading') return <Splash />;
  return <AuthForm />;
}

function Splash() {
  return (
    <Shell>
      <span className="text-sm text-[var(--color-text-muted)]">Loading…</span>
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="fixed inset-0 grid place-items-center bg-[var(--color-bg)] font-sans">
      {children}
    </div>
  );
}

function AuthForm() {
  const {
    status, signIn, completeNewPassword, requestReset, confirmReset,
    startForgot, backToSignIn, busy, error, notice, email: stateEmail,
  } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    switch (status) {
      case 'unauthenticated': void signIn(email.trim(), password); break;
      case 'newPassword':     void completeNewPassword(newPassword); break;
      case 'forgotRequest':   void requestReset(email.trim()); break;
      case 'forgotConfirm':   void confirmReset(code.trim(), newPassword); break;
    }
  };

  const heading =
    status === 'newPassword'   ? `Set a new password for ${stateEmail}` :
    status === 'forgotRequest' ? 'Reset your password' :
    status === 'forgotConfirm' ? `Enter the code sent to ${stateEmail}` :
    'Sign in to continue';

  const submitLabel =
    status === 'newPassword'   ? 'Set password' :
    status === 'forgotRequest' ? 'Send reset code' :
    status === 'forgotConfirm' ? 'Reset password' :
    'Sign in';

  return (
    <Shell>
      <form
        onSubmit={onSubmit}
        className="w-[320px] rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-[var(--shadow-2)]"
      >
        <h1 className="mb-1 font-mono text-sm font-semibold text-[var(--color-text)]">codeframe</h1>
        <p className="mb-5 text-xs text-[var(--color-text-muted)]">{heading}</p>

        {status === 'unauthenticated' && (
          <>
            <Field label="Email" type="email" value={email} autoFocus autoComplete="username" onChange={setEmail} />
            <Field label="Password" type="password" value={password} autoComplete="current-password" onChange={setPassword} />
          </>
        )}
        {status === 'newPassword' && (
          <Field label="New password" type="password" value={newPassword} autoFocus autoComplete="new-password" onChange={setNewPassword} />
        )}
        {status === 'forgotRequest' && (
          <Field label="Email" type="email" value={email} autoFocus autoComplete="username" onChange={setEmail} />
        )}
        {status === 'forgotConfirm' && (
          <>
            <Field label="Verification code" type="text" value={code} autoFocus autoComplete="one-time-code" onChange={setCode} />
            <Field label="New password" type="password" value={newPassword} autoComplete="new-password" onChange={setNewPassword} />
          </>
        )}

        {notice && <p className="mb-3 text-xs text-[var(--color-text-muted)]">{notice}</p>}
        {error && <p className="mb-3 text-xs text-[var(--color-danger)]">{error}</p>}

        <button
          type="submit"
          disabled={busy}
          className="mt-1 w-full rounded-md bg-[var(--color-accent)] px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
        >
          {busy ? 'Working…' : submitLabel}
        </button>

        {/* Footer links: forgot-password entry + back-out of the reset flow. */}
        <div className="mt-3 text-center text-xs">
          {status === 'unauthenticated' && (
            <button type="button" onClick={startForgot} className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
              Forgot password?
            </button>
          )}
          {(status === 'forgotRequest' || status === 'forgotConfirm') && (
            <button type="button" onClick={backToSignIn} className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
              Back to sign in
            </button>
          )}
        </div>
      </form>
    </Shell>
  );
}

function Field(props: {
  label: string; type: string; value: string; autoFocus?: boolean;
  autoComplete?: string; onChange: (v: string) => void;
}) {
  return (
    <label className="mb-3 block">
      <span className="mb-1 block text-xs text-[var(--color-text-muted)]">{props.label}</span>
      <input
        type={props.type}
        value={props.value}
        autoFocus={props.autoFocus}
        autoComplete={props.autoComplete}
        required
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full rounded-md border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-2.5 py-1.5 text-sm text-[var(--color-text)] outline-none focus-visible:border-[var(--color-accent)]"
      />
    </label>
  );
}
